import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../contexts/AuthContext";
import { getSearchIndexStatus, rebuildSearchIndex } from "../../services/api/chatSearchIndexApi";

const PERIOD_OPTIONS = [
  { value: "", label: "Todo o histórico" },
  { value: "30", label: "Últimos 30 dias" },
  { value: "90", label: "Últimos 90 dias" },
  { value: "365", label: "Último ano" },
];

const JOB_STATES = {
  RUNNING: { label: "Em andamento", className: "account-badge account-badge--trial" },
  COMPLETED: { label: "Concluída", className: "account-badge account-badge--paid" },
  FAILED: { label: "Falhou", className: "account-badge account-badge--error" },
};

const POLL_INTERVAL_MS = 4000;
const STATUS_CONCURRENCY = 4;

/** O backend devolve LocalDateTime já no horário de São Paulo, sem fuso. */
function formatLocalDateTime(value, withTime = true) {
  if (!value) return "—";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
  if (!match) return String(value);
  const [, y, m, d, hh, mm] = match;
  return withTime && hh ? `${d}/${m}/${y} ${hh}:${mm}` : `${d}/${m}/${y}`;
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("pt-BR");
}

function periodLabel(sinceDays) {
  if (!sinceDays) return "todo o histórico";
  return `últimos ${sinceDays} dias`;
}

const TIP_WIDTH = 280;
const TIP_MARGIN = 8;

/** O balão vai para o body: a tabela fica num contêiner com overflow, que o cortaria. */
function InfoTip({ children }) {
  const iconRef = useRef(null);
  const [position, setPosition] = useState(null);

  const show = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) return;
    const center = rect.left + rect.width / 2;
    const left = Math.min(
      Math.max(TIP_MARGIN, center - TIP_WIDTH / 2),
      window.innerWidth - TIP_WIDTH - TIP_MARGIN
    );
    setPosition({ top: rect.bottom + TIP_MARGIN, left, arrowLeft: center - left });
  };
  const hide = () => setPosition(null);

  return (
    <span
      ref={iconRef}
      className="info-tip"
      tabIndex={0}
      role="note"
      aria-label={typeof children === "string" ? children : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <span className="info-tip__icon" aria-hidden>
        i
      </span>
      {position
        ? createPortal(
            <span
              className="info-tip__bubble"
              style={{ top: position.top, left: position.left, width: TIP_WIDTH, "--arrow-left": `${position.arrowLeft}px` }}
            >
              {children}
            </span>,
            document.body
          )
        : null}
    </span>
  );
}

async function mapWithConcurrency(items, limit, fn) {
  const results = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await fn(items[index]);
    }
  });
  await Promise.all(workers);
  return results;
}

export function SearchIndexPanel() {
  const { companies } = useAuth();
  const [query, setQuery] = useState("");
  const [statuses, setStatuses] = useState({});
  const [periods, setPeriods] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyCompanyId, setBusyCompanyId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const filteredCompanies = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return companies;
    return companies.filter((company) => {
      const name = (company?.name || "").toLowerCase();
      const id = String(company?.id || "").toLowerCase();
      return name.includes(normalized) || id.includes(normalized);
    });
  }, [companies, query]);

  const fetchStatus = useCallback(async (companyId) => {
    try {
      const data = await getSearchIndexStatus(companyId);
      return [companyId, { status: "ok", data }];
    } catch (e) {
      return [companyId, { status: "error", message: e?.message || "Erro ao carregar" }];
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    const results = await mapWithConcurrency(companies, STATUS_CONCURRENCY, (company) =>
      fetchStatus(company.id)
    );
    setStatuses(Object.fromEntries(results));
    setLoading(false);
  }, [companies, fetchStatus]);

  useEffect(() => {
    if (!companies.length) {
      setLoading(false);
      return;
    }
    void loadAll();
  }, [companies, loadAll]);

  const runningIds = useMemo(
    () =>
      Object.entries(statuses)
        .filter(([, entry]) => entry?.status === "ok" && entry.data?.job?.state === "RUNNING")
        .map(([id]) => id),
    [statuses]
  );

  useEffect(() => {
    if (!runningIds.length) return undefined;
    const timer = setInterval(async () => {
      const results = await Promise.all(runningIds.map((id) => fetchStatus(id)));
      setStatuses((prev) => ({ ...prev, ...Object.fromEntries(results) }));
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [runningIds, fetchStatus]);

  const refreshCompany = async (companyId) => {
    const [id, entry] = await fetchStatus(companyId);
    setStatuses((prev) => ({ ...prev, [id]: entry }));
  };

  const handleRebuild = async (company) => {
    const sinceDays = periods[company.id] ?? "";
    setBusyCompanyId(company.id);
    setError("");
    setMessage("");
    try {
      await rebuildSearchIndex(company.id, sinceDays);
      setMessage(
        `Reindexação iniciada para "${company.name}" (${periodLabel(sinceDays)}). O andamento atualiza sozinho.`
      );
      await refreshCompany(company.id);
    } catch (e) {
      setError(e?.message || "Não foi possível iniciar a reindexação.");
      await refreshCompany(company.id);
    } finally {
      setBusyCompanyId(null);
    }
  };

  if (!loading && !companies.length) {
    return (
      <section className="card">
        <h2>Busca de mensagens</h2>
        <p>Seu usuário não possui empresas vinculadas no momento.</p>
      </section>
    );
  }

  return (
    <section className="card courtesy-panel">
      <div className="section-header">
        <div>
          <h2>Busca de mensagens</h2>
          <p className="section-description" style={{ marginBottom: 0 }}>
            Reindexe o histórico de mensagens de uma empresa para que a busca por conteúdo encontre
            mensagens antigas. Mensagens novas já são indexadas automaticamente.
          </p>
        </div>
        <span className="section-badge">{filteredCompanies.length} empresa(s)</span>
      </div>

      <div className="courtesy-toolbar">
        <input
          className="search-input"
          placeholder="Buscar por nome ou ID..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <button className="secondary-button" type="button" onClick={() => void loadAll()} disabled={loading}>
          Atualizar lista
        </button>
      </div>

      {message ? <div className="success-box">{message}</div> : null}
      {error ? <div className="error-box">{error}</div> : null}

      {loading ? (
        <p className="muted-text">Carregando situação do índice...</p>
      ) : (
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Empresa</th>
                <th>
                  Mensagens indexadas
                  <InfoTip>
                    Mensagens que já podem ser encontradas pela busca. Só entram mensagens com texto:
                    áudio, imagem ou figurinha sem legenda e mensagens só com emoji ficam de fora,
                    por isso este número costuma ser menor que o de mensagens lidas.
                  </InfoTip>
                </th>
                <th>
                  Última reindexação
                  <InfoTip>
                    Andamento da última reindexação disparada. "Mensagens lidas" conta todas as
                    mensagens do período, inclusive as sem texto. Esta informação é perdida quando a
                    API reinicia; a contagem de mensagens indexadas continua valendo.
                  </InfoTip>
                </th>
                <th>Período</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((company) => {
                const entry = statuses[company.id];
                const data = entry?.status === "ok" ? entry.data : null;
                const job = data?.job || null;
                const jobState = job ? JOB_STATES[job.state] : null;
                const isRunning = job?.state === "RUNNING";
                const isBusy = busyCompanyId === company.id;

                return (
                  <tr key={company.id}>
                    <td>
                      <strong>{company.name}</strong>
                      <div className="muted-text table-sub">{company.id}</div>
                    </td>
                    <td>
                      {entry?.status === "error" ? (
                        <span className="muted-text">{entry.message}</span>
                      ) : data ? (
                        <>
                          <strong>{formatNumber(data.indexedMessages)}</strong>
                          {data.indexedMessages > 0 ? (
                            <div className="muted-text table-sub">
                              de {formatLocalDateTime(data.oldestIndexed, false)} a{" "}
                              {formatLocalDateTime(data.newestIndexed, false)}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {jobState ? (
                        <>
                          <span className={jobState.className}>{jobState.label}</span>
                          <div className="muted-text table-sub">
                            {formatNumber(job.processed)} mensagens lidas · {periodLabel(job.sinceDays)}
                          </div>
                          <div className="muted-text table-sub">
                            Início {formatLocalDateTime(job.startedAt)}
                            {job.finishedAt ? ` · fim ${formatLocalDateTime(job.finishedAt)}` : ""}
                          </div>
                          {job.error ? <div className="muted-text table-sub">{job.error}</div> : null}
                        </>
                      ) : (
                        <span className="muted-text">Nenhuma desde a última subida da API</span>
                      )}
                    </td>
                    <td>
                      <select
                        className="table-input table-input--wide"
                        value={periods[company.id] ?? ""}
                        onChange={(event) =>
                          setPeriods((prev) => ({ ...prev, [company.id]: event.target.value }))
                        }
                        disabled={isBusy || isRunning}
                      >
                        {PERIOD_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="primary-button"
                          type="button"
                          onClick={() => void handleRebuild(company)}
                          disabled={isBusy || isRunning}
                        >
                          {isBusy ? "..." : isRunning ? "Reindexando..." : "Reindexar"}
                        </button>
                        <button
                          className="secondary-button"
                          type="button"
                          onClick={() => void refreshCompany(company.id)}
                          disabled={isBusy}
                        >
                          Atualizar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
